import { Dialog, Stack, type DialogProps } from '@mui/material';
import classnames from 'classnames';
import React, { type PropsWithChildren } from 'react';

import { Loader } from '@/components/shared/loader';

import { PanelWrapper, type PanelWrapperProps } from '../panels/panelWrapper';

export interface MainDialogProps extends PanelWrapperProps {
  isOpen: boolean;
  isLoading: boolean;
  onClose?: () => void;
  disableGutters?: boolean;
  /** Render as a full-viewport overlay instead of a centered modal. */
  fullScreen?: boolean;
  /**
   * Pass 0 for dialogs that replace one another (e.g. wizard-like step screens): each screen is its
   * own Dialog, so the default fade replays from transparent on every swap and reads as a flash.
   * An `{ enter, exit }` object animates the two directions independently.
   */
  transitionDuration?: DialogProps['transitionDuration'];
  /**
   * Fires after the exit transition finishes. Unmounting on close skips the exit fade entirely, so a
   * caller that wants one must flip `isOpen` to false and unmount from this callback instead.
   */
  onExited?: () => void;
  /**
   * Makes the panel body the scroll region when content exceeds the dialog's height cap: the header
   * and footer stay pinned and the scrollbar is clipped inside the corner radius. Off by default —
   * without it, overflowing content scrolls the whole paper, header and footer included.
   */
  scrollableBody?: boolean;
}

export function MainDialog(props: PropsWithChildren<MainDialogProps>) {
  const {
    isLoading,
    isOpen,
    header,
    footer,
    sx,
    disableGutters = false,
    fullScreen = false,
    transitionDuration,
    onExited,
    onClose,
    scrollableBody = false,
  } = props;

  return (
    <Dialog
      fullScreen={fullScreen}
      transitionDuration={transitionDuration}
      onClose={onClose ?? header?.onClose}
      onClick={(event) => event.stopPropagation()}
      open={isOpen}
      slotProps={{
        transition: { onExited },
        paper: {
          sx: fullScreen
            ? {
                width: '100%',
                height: '100%',
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: '0px',
                ...sx,
              }
            : {
                width: '40%',
                maxWidth: '840px',
                minWidth: '600px',
                height: 'max-content',
                minHeight: '300px',
                maxHeight: '90vh',
                borderRadius: '16px',
                ...(scrollableBody && { overflow: 'hidden' }),
                ...sx,
              },
        },
      }}
    >
      <PanelWrapper
        rounded
        header={
          header ? { ...header, onClose: header.onClose ?? onClose } : undefined
        }
        footer={footer ? { ...footer } : undefined}
        // A flex item's min-height defaults to its content, so without this the wrapper cannot
        // shrink to the paper's height cap and the body's own scroll region never engages.
        sx={scrollableBody ? { minHeight: 0 } : undefined}
      >
        <Stack
          className={classnames('h-full', {
            'py-[24px]': !disableGutters,
          })}
        >
          {isLoading && <Loader />}
          {!isLoading && <>{props.children}</>}
        </Stack>
      </PanelWrapper>
    </Dialog>
  );
}
